/// <reference lib="dom" />
import type { ChartLayer, ChartScale } from "../core/chart.ts";
import { type ChartAxis, ScaleIn } from "./shared.ts";

export interface MarkerDatum {
  value: number;
  label?: string;
  color?: string;
  dashed?: boolean;
  width?: number;
  opacity?: number;
  row?: number;
}

export interface MarkerLayerOpts {
  id?: string;
  axis?: ChartAxis;
  scale?: ChartScale;
  rowOffset?: number;
}

export interface MarkerLayer extends ChartLayer {
  set(markers: MarkerDatum[]): void;
}

export function MarkerLayer(opts: MarkerLayerOpts = {}): MarkerLayer {
  let markers: MarkerDatum[] = [];
  const axis = opts.axis ?? "x";
  return {
    id: opts.id ?? `markers-${axis}`,
    clip: true,
    set(next) {
      markers = next;
    },
    render(root, ctx) {
      const scale = ScaleIn(ctx, axis, opts.scale);
      const { inner } = ctx.frame;
      const { theme } = ctx;
      const g = root.selectAll<SVGGElement, MarkerDatum>("g.marker")
        .data(markers)
        .join((enter) => {
          const m = enter.append("g").attr("class", "marker");
          m.append("line");
          m.append("text");
          m.append("title");
          return m;
        });
      g.select("line")
        .attr("x1", (m) => (axis === "x" ? scale(m.value) : inner.left))
        .attr("x2", (m) => (axis === "x" ? scale(m.value) : inner.right))
        .attr("y1", (m) => (axis === "x" ? inner.top : scale(m.value)))
        .attr("y2", (m) => (axis === "x" ? inner.bottom : scale(m.value)))
        .attr("stroke", (m) => m.color ?? theme.accent)
        .attr("stroke-width", (m) => m.width ?? 1)
        .attr("stroke-opacity", (m) => m.opacity ?? theme.guideOpacity)
        .attr("stroke-dasharray", (m) => (m.dashed ? "3,2" : null))
        .style("pointer-events", "none");
      g.select("text")
        .attr("x", (m) => (axis === "x" ? scale(m.value) + 2 : inner.right - 2))
        .attr(
          "y",
          (m) =>
            axis === "x"
              ? inner.top + (theme.labelSize + 1) * (1 + (opts.rowOffset ?? 0) + (m.row ?? 0))
              : scale(m.value) - 2,
        )
        .attr("text-anchor", axis === "x" ? "start" : "end")
        .attr("font-size", theme.labelSize)
        .attr("fill", (m) => m.color ?? theme.accent)
        .style("pointer-events", "none")
        .text((m) => m.label ?? "");
      g.select("title").text((m) => `${m.label ?? ""} ${+m.value.toFixed(4)}`);
    },
  };
}
